import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { Icon, Skeleton } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { errorMessage } from '../../../../lib/api';
import { AddConnectionDialog, progressSteps } from '../../../settings/ConnectionsSettingsPage';
import {
  isMailConnection,
  useConnection,
  useConnections,
  useLinkConnection,
  type ConnectionItem,
} from '../../../settings/connections';
import { WizardButton } from '../../ui';
import { ActionError, StepFrame, useWizard } from '../StepFrame';
import { Bar, CardEyebrow, ChoiceCard, Panel, ProgressRows } from '../parts';
import styles from './MailboxStep.module.css';

type Choice = 'google' | 'imap';

const FAILED = new Set(['error', 'disconnected', 'credentials_required']);

/**
 * Nutzer 04/05 (M3): provider choice, the hosted Google link (same window — Unipile returns to the settings, the
 * setup session is routed back here) or the M3 IMAP form in a dialog; the connection's progress or failure.
 */
export function MailboxStep() {
  const mobile = useIsMobile();
  const wizard = useWizard();
  const connections = useConnections();
  const link = useLinkConnection();
  const [choice, setChoice] = useState<Choice>('google');
  const [imapOpen, setImapOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restart, setRestart] = useState(false);

  const mail = connections.data?.items.find(isMailConnection) ?? null;

  async function connectGoogle() {
    setError(null);
    try {
      const result = await link.mutateAsync({ provider: 'google', purposes: ['mail', 'calendar'] });
      window.location.assign(result.url);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  function primaryAction() {
    if (choice === 'google') void connectGoogle();
    else setImapOpen(true);
  }

  /** „Im Hintergrund fortsetzen": completes the step once the account is there, else moves on with it open. */
  async function continueInBackground() {
    const ok = await wizard.act('continue');
    if (!ok) wizard.goNext();
  }

  const skip = {
    label: mobile ? 'Später' : 'Später verbinden',
    onClick: () => void wizard.act('skip'),
    loading: wizard.acting === 'skip',
  };
  const back = wizard.goBack ? { label: 'Zurück', onClick: wizard.goBack } : undefined;

  if (connections.isPending) {
    return (
      <StepFrame title="Ihr Postfach verbinden" width={760} footer={null}>
        <Skeleton variant="block" height={180} />
      </StepFrame>
    );
  }

  if (mail && !restart && mail.status === 'connecting') {
    return (
      <StepFrame title="Postfach verbinden" intro={mobile} width={480} footer={null}>
        <ConnectingCard
          item={mail}
          busy={wizard.acting === 'continue'}
          onContinue={() => void continueInBackground()}
          onSettled={() => void connections.refetch()}
        />
        <ActionError />
      </StepFrame>
    );
  }

  if (mail && !restart && FAILED.has(mail.status)) {
    return (
      <StepFrame title="Postfach verbinden" intro={mobile} width={480} footer={null}>
        <Panel tone="danger" className={styles.stateCard}>
          <CardEyebrow>Schritt 2 von 8</CardEyebrow>
          <h2 className={styles.stateTitle}>Zugriff wurde abgebrochen</h2>
          <div className={styles.dangerBox}>{mail.error ?? mail.statusLabel}</div>
          <div className={styles.stateActions}>
            <WizardButton variant="primary" onClick={() => setRestart(true)}>
              Erneut versuchen
            </WizardButton>
            <WizardButton
              variant="secondary"
              loading={wizard.acting === 'skip'}
              onClick={() => void wizard.act('skip')}
            >
              Später verbinden
            </WizardButton>
          </div>
        </Panel>
        <ActionError />
      </StepFrame>
    );
  }

  const connected = mail !== null && mail.status === 'connected' && !restart;
  const primary = connected
    ? { label: 'Weiter', onClick: () => void wizard.act('continue'), loading: wizard.acting === 'continue' }
    : {
        label: choice === 'google' ? 'Mit Google verbinden' : 'Mit IMAP verbinden',
        onClick: primaryAction,
        loading: link.isPending,
      };

  return (
    <StepFrame
      title="Ihr Postfach verbinden"
      text="DAMOKLES ordnet ein- und ausgehende Mails automatisch den Kontakten und Deals zu. Gesendet wird weiter über Ihre Adresse."
      mobileTitle="Postfach verbinden"
      mobileText="DAMOKLES ordnet Mails automatisch Kontakten und Deals zu."
      width={760}
      footer={{ back, secondary: skip, primary }}
    >
      {connected && mail ? (
        <div className={styles.success} role="status">
          ✓ {mail.identifier ?? mail.displayName ?? 'Ihr Postfach'} ist verbunden
        </div>
      ) : null}
      {connected && mail?.error ? (
        <div className={styles.errorBanner} role="alert">
          {mail.error}
        </div>
      ) : null}
      <div role="radiogroup" aria-label="Anbieter" className={styles.choices}>
        <ChoiceCard
          icon="G"
          title="Google Workspace"
          text={mobile ? 'Mail und Kalender' : 'Mail und Kalender in einem Schritt'}
          badge={mobile ? undefined : 'Empfohlen'}
          selected={choice === 'google'}
          onSelect={() => setChoice('google')}
          compact={mobile}
        />
        <ChoiceCard
          icon={<Icon icon={Mail} size={16} />}
          title={mobile ? 'IMAP / SMTP' : 'IMAP / SMTP manuell'}
          text={mobile ? 'Serverdaten nötig' : 'Serveradressen und Ports nötig · fragen Sie Ihre IT'}
          selected={choice === 'imap'}
          onSelect={() => setChoice('imap')}
          compact={mobile}
        />
      </div>
      {error ? (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorTitle}>Zugriff abgebrochen</span>
          <span>{error}</span>
        </div>
      ) : null}
      {mobile ? null : (
        <div className={styles.privacy}>
          DAMOKLES liest Betreff, Empfänger und Inhalt, um Nachrichten zuzuordnen. Private Ordner können Sie
          im nächsten Schritt ausschließen. Zugriff jederzeit widerrufbar.
        </div>
      )}
      <ActionError />
      <AddConnectionDialog
        open={imapOpen}
        support={false}
        initialProvider="imap"
        onClose={() => setImapOpen(false)}
        onCreated={() => {
          setImapOpen(false);
          setRestart(false);
          void connections.refetch();
        }}
      />
    </StepFrame>
  );
}

/** Nutzer 05 left: „Postfach wird verbunden", the bar and the three rows of the connection's progress. */
function ConnectingCard({
  item,
  busy,
  onContinue,
  onSettled,
}: {
  item: ConnectionItem;
  busy: boolean;
  onContinue: () => void;
  /** The polled row left `connecting` (connected or failed): the step re-reads the list. */
  onSettled: () => void;
}) {
  // Outside the shell there is no event stream: the row is polled every 3 s while it connects.
  const polled = useConnection(item.id);
  const current = polled.data ?? item;
  const status = current.status;
  useEffect(() => {
    if (status !== 'connecting') onSettled();
  }, [status]);
  const rows = progressSteps(current);
  const done = rows.filter((row) => row.state === 'done').length;
  const percent = rows.length === 0 ? 0 : Math.max(8, Math.round(((done + 0.5) / rows.length) * 100));
  return (
    <Panel className={styles.stateCard}>
      <CardEyebrow>Schritt 2 von 8</CardEyebrow>
      <h2 className={styles.stateTitle}>Postfach wird verbunden</h2>
      <Bar percent={Math.min(100, percent)} label="Verbindungsaufbau" />
      <ProgressRows rows={rows} />
      <span className={styles.stateNote}>Sie können weitermachen, der Abgleich läuft im Hintergrund.</span>
      <WizardButton variant="secondary" loading={busy} onClick={onContinue}>
        Im Hintergrund fortsetzen
      </WizardButton>
    </Panel>
  );
}
